import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { forkJoin, from } from 'rxjs';
import { emailTypeOptions } from 'src/app/classes/models/email-type';
import { emailsService } from 'src/app/shared/vive-api/emails.service';
import {
  EmailDto,
  EmailsFindAllRequestDtoEmailTypeEnum,
  SendGridEventDto
} from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

// Every request here leaves the loading mask off: the page shows its own state.
const requestOptions = {
  context: { SkipLoadingMask: true },
  requestTimeout: LOAD_BALANCER_TIMEOUT_MS
};

@Component({
  selector: 'app-admin-admin-emails',
  templateUrl: './admin-admin-emails.component.html',
  styleUrl: './admin-admin-emails.component.less',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminAdminEmailsComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);

  emails: EmailDto[] = [];
  loading = false;
  totalCount = 0;
  currentPage = 1;
  pageSize = 25;

  drawerOpen = false;
  drawerLoading = false;
  selectedEmail: EmailDto | null = null;
  selectedEmailId: number | null = null;
  sendGridEvents: SendGridEventDto[] = [];

  searchForm = new FormGroup({
    to: new FormControl(''),
    emailType: new FormControl<EmailsFindAllRequestDtoEmailTypeEnum | ''>(''),
    createdAtStart: new FormControl(''),
    createdAtEnd: new FormControl('')
  });

  emailTypes = emailTypeOptions();

  ngOnInit(): void {
    this.loadEmails();
  }

  search(): void {
    this.currentPage = 1;
    this.loadEmails();
  }

  resetFilters(): void {
    this.searchForm.reset();
    this.currentPage = 1;
    this.loadEmails();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.loadEmails();
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(this.totalPages, this.currentPage + 2);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  openDetail(emailId: number): void {
    this.selectedEmailId = emailId;
    this.selectedEmail = null;
    this.sendGridEvents = [];
    this.drawerLoading = true;
    this.drawerOpen = true;
    this._cdr.markForCheck();

    forkJoin([
      from(
        emailsService.findOneById({
          pathParams: { emailId },
          options: requestOptions
        })
      ),
      from(
        emailsService.findAllEvents({
          pathParams: { emailId },
          options: requestOptions
        })
      )
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: ([email, eventsResponse]) => {
          this.selectedEmail = email;
          this.sendGridEvents = eventsResponse.data;
          this.drawerLoading = false;
          this._cdr.markForCheck();
        },
        error: () => {
          this.drawerLoading = false;
          this._cdr.markForCheck();
        }
      });
  }

  closeDrawer(): void {
    this.drawerOpen = false;
    this.selectedEmail = null;
    this.selectedEmailId = null;
    this.sendGridEvents = [];
    this._cdr.markForCheck();
  }

  // `updatedAt` is optional on the generated EmailDto.
  formatDate(date: string | Date | undefined): string {
    if (!date) {
      return '';
    }
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  private loadEmails(): void {
    this.loading = true;
    const formValue = this.searchForm.value;

    from(
      emailsService.findAll({
        queryParams: {
          ...(formValue.to && { to: formValue.to }),
          ...(formValue.emailType && { emailType: formValue.emailType }),
          ...(formValue.createdAtStart && {
            createdAtStart: new Date(formValue.createdAtStart).toISOString()
          }),
          ...(formValue.createdAtEnd && {
            createdAtEnd: new Date(formValue.createdAtEnd).toISOString()
          }),
          page: this.currentPage,
          pageSize: this.pageSize,
          includeCount: true
        },
        options: requestOptions
      })
    )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.emails = response.data;
          this.totalCount = response.meta.total ?? 0;
          this.loading = false;
          this._cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this._cdr.markForCheck();
        }
      });
  }
}
